import axios from 'axios';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Setting, Subscription } from '../App';
import { CUSTOMER_API_URL } from '../constants';

type Product = {
  productId: string;
  title: string;
  onlineStorePreviewUrl?: string;
  images: string[];
  variants: {
    variantId: string;
    requiresShipping: boolean;
  }[];
};

window.addEventListener('message', (event) => {
  const alternativeProductReceivedData = event.data;
  if (alternativeProductReceivedData !== 'stopMonitor') {
    if (typeof alternativeProductReceivedData === 'string') {
      try {
        const parseReceivedData = JSON.parse(alternativeProductReceivedData);
        if (parseReceivedData?.alternativeProducts) {
          const alternativeProductString = JSON.stringify(parseReceivedData.alternativeProducts);
          const alternativeProductElement = document.querySelector('#alternativeProductElement');

          if (!alternativeProductElement) {
            const body = document.querySelector('body');
            const dataInput = document.createElement('div');
            dataInput.setAttribute('data-value', alternativeProductString);
            dataInput.id = 'alternativeProductElement';
            dataInput.style.display = 'none';

            if (body) body.appendChild(dataInput);
          } else {
            alternativeProductElement.setAttribute('data-value', alternativeProductString);
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
  }
});

window.addEventListener('message', (event) => {
  const receivedData = event.data;
  if (receivedData !== 'stopMonitor') {
    if (typeof receivedData === 'string') {
      try {
        const parseReceivedData = JSON.parse(receivedData);
        if (parseReceivedData?.hbSubscriptionProducts) {
          const parseHbSubscriptionProducts = JSON.stringify(parseReceivedData.hbSubscriptionProducts);
          const hbSubscriptionProductDataElement = document.querySelector('#hbSubscriptionProductData');

          if (!hbSubscriptionProductDataElement) {
            const body = document.querySelector('body');
            const dataInput = document.createElement('div');
            dataInput.setAttribute('data-value', parseHbSubscriptionProducts);
            dataInput.id = 'hbSubscriptionProductData';
            dataInput.style.display = 'none';

            if (body) body.appendChild(dataInput);
          } else {
            hbSubscriptionProductDataElement.setAttribute('data-value', parseHbSubscriptionProducts);
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
  }
});

export const useSubscriptionLineEditProduct = (setting: Setting) => {
  const [subscription, setSubscription] = useState<Subscription>();
  const [products, setProducts] = useState<Product[]>();
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [infoMessage, setInfoMessage] = useState<string>('ローディング中...');
  const { id, lineId } = useParams<{ id: string; lineId: string }>();

  useEffect(() => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        {
          subscriptionId: id,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({ ...response.data, disabled: false });
      })
      .catch((e) => {
        console.log(e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!subscription) return;

    const decodedLineId = decodeURIComponent(lineId);
    let line = subscription.subscriptionLines.find((line) => line.lineId === decodedLineId);
    if (!line) {
      line = subscription.subscriptionLines[0];
    }

    if (lineId === 'add') {
      const hbSubscriptionProductDataElement = document.querySelector('#hbSubscriptionProductData');
      if (hbSubscriptionProductDataElement) {
        const storageHbSubscriptionProducts = hbSubscriptionProductDataElement.getAttribute('data-value');
        if (storageHbSubscriptionProducts) {
          const hbSubscriptionProducts = JSON.parse(storageHbSubscriptionProducts)
          setProducts(hbSubscriptionProducts);
          setHasNextPage(false);

          if (hbSubscriptionProducts.length === 0) {
            setInfoMessage('この商品は変更できません。<br/>店舗にお問い合わせください。');
          }
        } else {
          setInfoMessage('この商品は変更できません。<br/>店舗にお問い合わせください。');
        }
      }
    } else {
      axios
          .post(
              `${CUSTOMER_API_URL}?action=getAlternativeProducts`,
              {
                page,
                sellingPlanId: line.sellingPlanId,
              },
              {
                headers: { Authorization: setting.token },
              }
          )
          .then((response) => {
            const alternativeProductElement = document.querySelector('#alternativeProductElement');
            if (alternativeProductElement) {
              const alternativeProductString = alternativeProductElement.getAttribute('data-value');
              if (alternativeProductString) {
                const dataSortCompare = JSON.parse(alternativeProductString)

                response.data.products.sort((a, b) => {
                  const productIdA = a.productId;
                  const productIdB = b.productId;

                  const indexA = dataSortCompare.findIndex(item => item.productId === productIdA);
                  const indexB = dataSortCompare.findIndex(item => item.productId === productIdB);

                  return indexA - indexB;
                });
              }
            }

            setProducts(response.data.products);
            setHasNextPage(response.data.hasNextPage);

            if (response.data.products.length === 0) {
              setInfoMessage('この商品は変更できません。<br/>店舗にお問い合わせください。');
            }
          })
          .catch((e) => {
            console.log(e.message);
            setInfoMessage('この商品は変更できません。<br/>店舗にお問い合わせください。');
          });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [subscription]);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

    setting.resize(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const paginate = (nextPage: number) => {
    if (!subscription) return;

    const decodedLineId = decodeURIComponent(lineId);
    let line = subscription.subscriptionLines.find((line) => line.lineId === decodedLineId);
    if (!line) {
      line = subscription.subscriptionLines[0];
    }

    setPage(nextPage);

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getAlternativeProducts`,
        {
          page: nextPage,
          sellingPlanId: line.sellingPlanId,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setProducts(response.data.products);
        setHasNextPage(response.data.hasNextPage);
      })
      .catch((e) => {
        console.log(e.message);
      });
  };

  return {
    subscription,
    products,
    lineId,
    infoMessage,
    page,
    hasNextPage,
    paginate,
  };
};